// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 62.47.0 (pack dependencies D5, D6, D11) — the scope-determination
 * option attributes and the requirement tier condition resolve inside the pack.
 *
 * A product-identification register reads, off the installed scope template,
 * which option is compulsory, which needs the instrument relied on, which
 * raises the duties that wait on the risk tier, and which instruments are
 * offered as a cited basis; and, off the framework record, which duties wait
 * on the tier. A malformed declaration fails silently in the direction that
 * matters: an option with no class would be counted in no class (or merged), a
 * raising option on a voluntary class would raise compulsory duties for a
 * voluntary product line, a tier-conditioned duty no option can raise would
 * never apply to anyone, and a cited basis that names no tracked instrument
 * would be offered with nothing behind it. Each is refused here, by name.
 *
 * Pure over the parsed `frameworks.json` and `forms.json` of one pack, so every
 * rule is proven red on a planted shape. Names no instrument, option code or
 * framework (R-46): the attribute names and the participation vocabulary are
 * the platform's (`types/product-identification.ts`).
 */
import { PRODUCT_IDENTIFICATION_PACK_ATTR as ATTR, PRODUCT_IDENTIFICATION_REGISTER_SURFACES, SCOPE_PARTICIPATION_CLASSES, } from '../types/product-identification.js';
/** The follow-on list key on a framework record (`localization/follow-on-item-reader.ts`). */
const TRACKED_FOLLOW_ON_FIELD = 'tracked_follow_on_instruments';
const OPTION_ATTRS = [
    ATTR.participation,
    ATTR.requiresInstrument,
    ATTR.raisesTierConditionedDuties,
    ATTR.citesFrameworkCode,
    ATTR.raisesTierConditionedDutiesVoluntarily,
];
const object = (value) => !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value) => typeof value === 'string' && value.trim() !== '';
function frameworkCodesOf(form) {
    const codes = new Set();
    if (text(form.framework_code))
        codes.add(form.framework_code);
    if (Array.isArray(form.framework_codes))
        for (const c of form.framework_codes)
            if (text(c))
                codes.add(c);
    return [...codes];
}
export function productIdentificationDeclarationErrors(frameworks, forms) {
    const out = [];
    const fws = (Array.isArray(frameworks) ? frameworks : []).filter(object);
    const byCode = new Map();
    for (const fw of fws)
        if (text(fw.framework_code))
            byCode.set(fw.framework_code, fw);
    // ── Requirements: `tier_conditioned` is a boolean, and only on a duty. ──
    const tierConditioned = new Map(); // framework → requirement ids
    (Array.isArray(frameworks) ? frameworks : []).forEach((fw, fi) => {
        if (!object(fw))
            return;
        const code = text(fw.framework_code) ? fw.framework_code : `[${fi}]`;
        (Array.isArray(fw.requirements) ? fw.requirements : []).forEach((req, ri) => {
            if (!object(req) || req[ATTR.tierConditioned] === undefined)
                return;
            const at = `$[${fi}].requirements[${ri}].${ATTR.tierConditioned}`;
            const id = text(req.requirement_id) ? req.requirement_id : `requirements[${ri}]`;
            const value = req[ATTR.tierConditioned];
            if (typeof value !== 'boolean') {
                out.push({
                    file: 'frameworks',
                    json_path: at,
                    rule: 'TIER_CONDITION_INVALID',
                    message: `${code} ${id}: ${ATTR.tierConditioned} must be true or false; got ${JSON.stringify(value)}`,
                });
                return;
            }
            if (value && req.mandatory !== true) {
                out.push({
                    file: 'frameworks',
                    json_path: at,
                    rule: 'TIER_CONDITION_NOT_A_DUTY',
                    message: `${code} ${id}: ${ATTR.tierConditioned} is true on a requirement that is not mandatory; a voluntary practice is never raised as a duty, so the declaration would be read by nothing`,
                });
                return;
            }
            if (value)
                tierConditioned.set(code, [...(tierConditioned.get(code) ?? []), id]);
        });
    });
    // ── Forms: option attributes only on the scope surface, well formed. ──
    const scopeSurface = PRODUCT_IDENTIFICATION_REGISTER_SURFACES.scopeDetermination;
    const raisingFrameworks = new Map(); // framework → json path of a raising option
    // Pack 1.0.17 — an option showing the tier-conditioned duties to a voluntary participant.
    const voluntaryRaisingFrameworks = new Map();
    (Array.isArray(forms) ? forms : []).forEach((form, i) => {
        if (!object(form))
            return;
        const schemaId = text(form.schema_id) ? form.schema_id : `[${i}]`;
        const onScope = form.register_surface === scopeSurface;
        const formFrameworks = frameworkCodesOf(form);
        const trackedBasis = new Set();
        for (const code of formFrameworks) {
            const items = byCode.get(code)?.[TRACKED_FOLLOW_ON_FIELD];
            for (const item of Array.isArray(items) ? items : []) {
                if (!object(item) || !Array.isArray(item.existing_basis))
                    continue;
                for (const b of item.existing_basis)
                    if (text(b))
                        trackedBasis.add(b);
            }
        }
        let participationFields = 0;
        const voluntaryFields = new Set();
        (Array.isArray(form.sections) ? form.sections : []).forEach((section, si) => {
            if (!object(section))
                return;
            (Array.isArray(section.fields) ? section.fields : []).forEach((field, fi) => {
                if (!object(field) || !Array.isArray(field.options))
                    return;
                const fieldAt = `$[${i}].sections[${si}].fields[${fi}]`;
                const fieldId = text(field.field_id) ? field.field_id : `[${fi}]`;
                const options = field.options.filter(object);
                const withClass = options.filter((o) => o[ATTR.participation] !== undefined).length;
                if (onScope && withClass > 0) {
                    participationFields += 1;
                    if (withClass !== options.length) {
                        out.push({
                            file: 'forms',
                            json_path: `${fieldAt}.options`,
                            rule: 'SCOPE_PARTICIPATION_INCOMPLETE',
                            message: `${schemaId} ${fieldId}: ${withClass} of ${options.length} options declare ${ATTR.participation}; an option with no class would be counted in no class, or merged with another`,
                        });
                    }
                }
                field.options.forEach((option, oi) => {
                    if (!object(option))
                        return;
                    const at = `${fieldAt}.options[${oi}]`;
                    const value = text(option.value) ? option.value : `[${oi}]`;
                    const present = OPTION_ATTRS.filter((a) => option[a] !== undefined);
                    if (present.length === 0)
                        return;
                    if (!onScope) {
                        out.push({
                            file: 'forms',
                            json_path: `${at}.${present[0]}`,
                            rule: 'SCOPE_OPTION_ATTRIBUTE_MISPLACED',
                            message: `${schemaId} ${fieldId} option ${value} declares ${present.join(', ')} on a template that does not declare register_surface ${scopeSurface}; no register reads it there`,
                        });
                        return;
                    }
                    const invalid = (attr, why) => {
                        out.push({
                            file: 'forms',
                            json_path: `${at}.${attr}`,
                            rule: 'SCOPE_OPTION_ATTRIBUTE_INVALID',
                            message: `${schemaId} ${fieldId} option ${value}: ${why}`,
                        });
                    };
                    for (const attr of [
                        ATTR.requiresInstrument,
                        ATTR.raisesTierConditionedDuties,
                        ATTR.raisesTierConditionedDutiesVoluntarily,
                    ]) {
                        if (option[attr] !== undefined && typeof option[attr] !== 'boolean') {
                            invalid(attr, `${attr} must be true or false; got ${JSON.stringify(option[attr])}`);
                        }
                    }
                    const cls = option[ATTR.participation];
                    if (cls !== undefined &&
                        !SCOPE_PARTICIPATION_CLASSES.includes(cls)) {
                        invalid(ATTR.participation, `${ATTR.participation} ${JSON.stringify(cls)} is not one of ${SCOPE_PARTICIPATION_CLASSES.join(' | ')}`);
                    }
                    const cited = option[ATTR.citesFrameworkCode];
                    if (cited !== undefined) {
                        if (cls !== undefined) {
                            invalid(ATTR.citesFrameworkCode, `an option is either a determination option (${ATTR.participation}) or a cited basis (${ATTR.citesFrameworkCode}), never both`);
                        }
                        else if (!text(cited)) {
                            invalid(ATTR.citesFrameworkCode, `${ATTR.citesFrameworkCode} must be a framework code`);
                        }
                        else if (!trackedBasis.has(cited)) {
                            out.push({
                                file: 'forms',
                                json_path: `${at}.${ATTR.citesFrameworkCode}`,
                                rule: 'SCOPE_CITED_BASIS_UNRESOLVED',
                                message: `${schemaId} ${fieldId} option ${value} cites ${cited}, which no tracked follow-on item of ${formFrameworks.join(', ') || 'the template'} names as an existing basis; the option would be offered with nothing behind it`,
                            });
                        }
                    }
                    // Pack 1.0.17 (legal reading P2-1) — the voluntary display is declared on
                    // the participation decision, never on a determination option: a
                    // determination option either raises the duties as compulsory or not.
                    const voluntary = option[ATTR.raisesTierConditionedDutiesVoluntarily];
                    if (voluntary !== undefined && cls !== undefined) {
                        invalid(ATTR.raisesTierConditionedDutiesVoluntarily, `${ATTR.raisesTierConditionedDutiesVoluntarily} belongs on an option of the voluntary-participation decision, never on a determination option (${ATTR.participation})`);
                    }
                    if (voluntary === true && cls === undefined) {
                        voluntaryFields.add(fieldId);
                        for (const code of formFrameworks)
                            if (!voluntaryRaisingFrameworks.has(code))
                                voluntaryRaisingFrameworks.set(code, at);
                    }
                    if (option[ATTR.raisesTierConditionedDuties] === true) {
                        if (cls !== 'compulsory') {
                            out.push({
                                file: 'forms',
                                json_path: `${at}.${ATTR.raisesTierConditionedDuties}`,
                                rule: 'SCOPE_RAISING_OPTION_NOT_COMPULSORY',
                                message: `${schemaId} ${fieldId} option ${value} raises the tier-conditioned duties but its ${ATTR.participation} is ${JSON.stringify(cls ?? null)}, not "compulsory"; a compulsory duty would be raised for a line that is not compulsory`,
                            });
                        }
                        for (const code of formFrameworks)
                            if (!raisingFrameworks.has(code))
                                raisingFrameworks.set(code, at);
                    }
                });
            });
        });
        if (onScope && voluntaryFields.size > 1) {
            out.push({
                file: 'forms',
                json_path: `$[${i}].sections`,
                rule: 'SCOPE_VOLUNTARY_RAISING_FIELD_COUNT',
                message: `${schemaId} declares ${ATTR.raisesTierConditionedDutiesVoluntarily} on options of ${voluntaryFields.size} fields (${[...voluntaryFields].join(', ')}); exactly one field is the voluntary-participation decision the register reads`,
            });
        }
        if (onScope && participationFields !== 1) {
            out.push({
                file: 'forms',
                json_path: `$[${i}].sections`,
                rule: 'SCOPE_PARTICIPATION_FIELD_COUNT',
                message: `${schemaId} declares register_surface ${scopeSurface} with ${participationFields} fields whose options carry ${ATTR.participation}; exactly one field is the determination the register reads`,
            });
        }
    });
    // ── Both directions: a tier-conditioned duty some option raises, and a raising option with a duty to raise. ──
    for (const [code, ids] of tierConditioned) {
        if (!raisingFrameworks.has(code)) {
            out.push({
                file: 'frameworks',
                json_path: `$[?(@.framework_code=='${code}')].requirements`,
                rule: 'TIER_CONDITION_UNRAISABLE',
                message: `${code}: ${ids.length} requirement(s) carry ${ATTR.tierConditioned} (${ids.join(', ')}) and no option of a ${scopeSurface} template of this framework in the pack carries ${ATTR.raisesTierConditionedDuties}; those duties could never apply to anyone`,
            });
        }
    }
    for (const [code, at] of raisingFrameworks) {
        if (byCode.has(code) && !tierConditioned.has(code)) {
            out.push({
                file: 'forms',
                json_path: `${at}.${ATTR.raisesTierConditionedDuties}`,
                rule: 'TIER_CONDITION_RAISES_NOTHING',
                message: `${code}: an option raises the tier-conditioned duties and no requirement of the framework carries ${ATTR.tierConditioned}; the option would raise nothing`,
            });
        }
    }
    for (const [code, at] of voluntaryRaisingFrameworks) {
        if (byCode.has(code) && !tierConditioned.has(code)) {
            out.push({
                file: 'forms',
                json_path: `${at}.${ATTR.raisesTierConditionedDutiesVoluntarily}`,
                rule: 'TIER_CONDITION_RAISES_NOTHING',
                message: `${code}: an option shows the tier-conditioned duties to a voluntary participant and no requirement of the framework carries ${ATTR.tierConditioned}; the option would show nothing`,
            });
        }
    }
    return out;
}
//# sourceMappingURL=product-identification-declaration.js.map